import { AssignmentJob, AssignmentStage } from 'constants/assignment'
import { DocumentCategory, ProductKindsWithDocumentDraftCheck } from 'constants/product'
import { isEditingCategory, isShootingCategory } from 'utils/validators/categoryUtils'

import { ProductDTO } from 'models/product'
import { RealEstatePropertyAssignmentProductsDTO } from 'models/property'

interface ProgressStep {
  step: number
  jobDone: string
  jobTodo: string
  activeStep: boolean
}

const shootingWithCTSteps = [
  { step: 1, jobDone: AssignmentJob.ORDER_RECEIVED, jobTodo: AssignmentJob.MATCHING_CREATIVE, activeStep: false },
  { step: 2, jobDone: AssignmentJob.CREATIVE_BOOKED, jobTodo: AssignmentJob.WAITING_FOR_SHOOTING, activeStep: false },
  { step: 3, jobDone: AssignmentJob.SHOOTING_PERFORMED, jobTodo: AssignmentJob.EDITING_VISUALS, activeStep: false },
  { step: 4, jobDone: AssignmentJob.ORDER_DELIVERED, jobTodo: '', activeStep: false },
  { step: 4, jobDone: AssignmentJob.ASSIGNMENT_CANCELLED, jobTodo: '', activeStep: false }
]

const shootingWithCTwithStagingSteps = [
  { step: 1, jobDone: AssignmentJob.ORDER_RECEIVED, jobTodo: AssignmentJob.MATCHING_CREATIVE, activeStep: false },
  { step: 2, jobDone: AssignmentJob.CREATIVE_BOOKED, jobTodo: AssignmentJob.WAITING_FOR_SHOOTING, activeStep: false },
  { step: 3, jobDone: AssignmentJob.SHOOTING_PERFORMED, jobTodo: AssignmentJob.EDITING_VISUALS, activeStep: false },
  { step: 4, jobDone: AssignmentJob.ORDER_DELIVERED_STAGING_ACTION_TODO, jobTodo: AssignmentJob.ORDER_DELIVERED_STAGING_ACTION_IN_PROGRESS, activeStep: false },
  { step: 5, jobDone: AssignmentJob.ORDER_DELIVERED_STAGING_ACTION_IN_PROGRESS, jobTodo: AssignmentJob.ORDER_DELIVERED_STAGING_ACTION_DONE, activeStep: false },
  { step: 6, jobDone: AssignmentJob.ORDER_DELIVERED_STAGING_ACTION_DONE, jobTodo: '', activeStep: false },
  { step: 6, jobDone: AssignmentJob.ASSIGNMENT_CANCELLED, jobTodo: '', activeStep: false }
]

const editingWithShootingSteps = [
  { step: 1, jobDone: AssignmentJob.ORDER_RECEIVED, jobTodo: AssignmentJob.WAITING_FOR_SELECTION, activeStep: false },
  { step: 2, jobDone: AssignmentJob.SELECTION_DONE, jobTodo: AssignmentJob.EDITING_VISUALS, activeStep: false },
  { step: 3, jobDone: AssignmentJob.ORDER_DELIVERED, jobTodo: '', activeStep: false },
  { step: 3, jobDone: AssignmentJob.ASSIGNMENT_CANCELLED, jobTodo: '', activeStep: false }
]

const editingWithoutShootingSteps = [
  { step: 1, jobDone: AssignmentJob.ORDER_RECEIVED, jobTodo: AssignmentJob.EDITING_VISUALS, activeStep: false },
  { step: 2, jobDone: AssignmentJob.ORDER_DELIVERED, jobTodo: '', activeStep: false },
  { step: 2, jobDone: AssignmentJob.ASSIGNMENT_CANCELLED, jobTodo: '', activeStep: false }
]

const authoritiesDocumentsSteps = [
  { step: 1, jobDone: AssignmentJob.AWAITING_SIGNATURE, jobTodo: AssignmentJob.DOCUMENT_SIGNED, activeStep: false },
  { step: 2, jobDone: AssignmentJob.DOCUMENTS_IN_PROGRESS, jobTodo: AssignmentJob.ORDER_DELIVERED, activeStep: false },
  { step: 3, jobDone: AssignmentJob.ORDER_DELIVERED, jobTodo: '', activeStep: false },
  { step: 3, jobDone: AssignmentJob.ASSIGNMENT_CANCELLED, jobTodo: '', activeStep: false },
]

const documentsSteps = [
  { step: 1, jobDone: AssignmentJob.AWAITING_SIGNATURE, jobTodo: AssignmentJob.DOCUMENT_SIGNED, activeStep: false },
  { step: 2, jobDone: AssignmentJob.DOCUMENTS_IN_PROGRESS, jobTodo: AssignmentJob.VALIDATE_DRAFT, activeStep: false },
  { step: 3, jobDone: AssignmentJob.VALIDATE_DRAFT, jobTodo: AssignmentJob.ORDER_DELIVERED, activeStep: false },
  { step: 4, jobDone: AssignmentJob.ORDER_DELIVERED, jobTodo: '', activeStep: false },
  { step: 4, jobDone: AssignmentJob.ASSIGNMENT_CANCELLED, jobTodo: '', activeStep: false },
]

export const getProgressCategoryData = (
  stage: AssignmentStage,
  products: ProductDTO[] | RealEstatePropertyAssignmentProductsDTO[],
  isAssignmentWithStagingTodo?: boolean,
  isAssignmentWithStagingInProgress?: boolean,
  isStagingDone?: boolean,
  isAssignmentCancelled?: boolean
): Array<ProgressStep> => {
  if (!products[0]) return []

  let data = []

  const productCategory = products[0].category
  const isAuthoritiesDocument = productCategory === DocumentCategory.AUTHORITIES_DOCUMENTS
  const isDocumentWithDraftCheck = ProductKindsWithDocumentDraftCheck.has(products[0].kind)
  const isSomeProductWithCT = products.some(product => product.requiresCreative)

  /** ASSIGNMENT WITH STAGING TODO OR STAGING IN PROGRESS */
  const isShootingWithCT = isShootingCategory(productCategory) && isSomeProductWithCT
  const isStagingInvolved = isAssignmentWithStagingTodo || isAssignmentWithStagingInProgress

  if (isShootingWithCT && isStagingInvolved) {
    data = shootingWithCTwithStagingSteps.map(step => ({ ...step }))

    if (isAssignmentCancelled) {
      data[6].activeStep = true
    } else {
      switch (stage) {
        case AssignmentStage.MISSION_ORDER_PLACED:
        case AssignmentStage.PRE_PRODUCTION:
        case AssignmentStage.AUTOMATED:
        case AssignmentStage.WAITING_FOR_CT_TO_ACCEPT:
          data[0].activeStep = true
          break

        case AssignmentStage.CREATIVE_BOOKED:
          data[1].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_BY_CT_TO_BKBN:
        case AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR:
        case AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN:
          data[2].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_TO_CLIENT:
          if (isAssignmentWithStagingTodo) {
            data[3].activeStep = true
          } else if (isAssignmentWithStagingInProgress) {
            data[4].activeStep = true
          } else if (isStagingDone) {
            data[5].activeStep = true
          }

          break

        default:
          break
      }
    }

    /** SHOOTING WITH CT PROGRESS */
  } else if (isShootingCategory(productCategory) && isSomeProductWithCT) {
    data = shootingWithCTSteps.map(step => ({ ...step }))

    if (isAssignmentCancelled) {
      data[4].activeStep = true
    } else {
      switch (stage) {
        case AssignmentStage.MISSION_ORDER_PLACED:
        case AssignmentStage.PRE_PRODUCTION:
        case AssignmentStage.AUTOMATED:
        case AssignmentStage.WAITING_FOR_CT_TO_ACCEPT:
          data[0].activeStep = true
          break

        case AssignmentStage.CREATIVE_BOOKED:
          data[1].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_BY_CT_TO_BKBN:
        case AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR:
        case AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN:
          data[2].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_TO_CLIENT:
          data[3].activeStep = true
          break

        default:
          break
      }
    }

    /** EDITING WITH SHOOTING */
  } else if (isShootingCategory(productCategory) && !isSomeProductWithCT) {
    data = editingWithShootingSteps.map(step => ({ ...step }))

    if (isAssignmentCancelled) {
      data[3].activeStep = true
    } else {
      switch (stage) {
        case AssignmentStage.MISSION_ORDER_PLACED:
        case AssignmentStage.PRE_PRODUCTION:
        case AssignmentStage.AUTOMATED:
        case AssignmentStage.WAITING_FOR_CT_TO_ACCEPT:
        case AssignmentStage.CREATIVE_BOOKED:
          data[0].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_BY_CT_TO_BKBN:
        case AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR:
        case AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN:
          data[1].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_TO_CLIENT:
          data[2].activeStep = true
          break

        default:
          break
      }
    }

    /** EDITING WITHOUT SHOOTING */
  } else if (isEditingCategory(productCategory) && !isDocumentWithDraftCheck) {
    data = editingWithoutShootingSteps.map(step => ({ ...step }))

    if (isAssignmentCancelled) {
      data[2].activeStep = true
    } else {
      switch (stage) {
        case AssignmentStage.MISSION_ORDER_PLACED:
        case AssignmentStage.PRE_PRODUCTION:
        case AssignmentStage.AUTOMATED:
        case AssignmentStage.WAITING_FOR_CT_TO_ACCEPT:
        case AssignmentStage.CREATIVE_BOOKED:
        case AssignmentStage.VISUALS_SENT_BY_CT_TO_BKBN:
        case AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR:
        case AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN:
          data[0].activeStep = true
          break

        case AssignmentStage.VISUALS_SENT_TO_CLIENT:
          data[1].activeStep = true
          break

        default:
          break
      }
    }

    /** DOCUMENTS */
  } else if (isAuthoritiesDocument) {
    data = authoritiesDocumentsSteps.map(step => ({ ...step }))

    if (isAssignmentCancelled) {
      data[3].activeStep = true
    } else {
      switch (stage) {
        case AssignmentStage.WAITING_FOR_POWER_OF_ATTORNEY_DOCUMENTS:
          data[0].activeStep = true
          break
        case AssignmentStage.PRE_PROCESSING_ORDER:
        case AssignmentStage.WAITING_FOR_CLIENT_TO_UPLOAD_DOCUMENTS:
        case AssignmentStage.REQUEST_SENT_TO_PROCESSOR:
        case AssignmentStage.DOCUMENTS_RECEIVED_FROM_PROCESSOR:
        case AssignmentStage.DOCUMENTS_APPROVED_FROM_CLIENT:
          data[1].activeStep = true
          break

        case AssignmentStage.DOCUMENTS_SENT_TO_CLIENT:
          data[2].activeStep = true
          break

        default:
          break
      }
    }

    /** DOCUMENTS WITH DRAFT */
  } else if (isDocumentWithDraftCheck) {
    data = documentsSteps.map(step => ({ ...step }))

    if (isAssignmentCancelled) {
      data[4].activeStep = true
    } else {
      switch (stage) {
        case AssignmentStage.WAITING_FOR_POWER_OF_ATTORNEY_DOCUMENTS:
          data[0].activeStep = true
          break
        case AssignmentStage.PRE_PROCESSING_ORDER:
        case AssignmentStage.WAITING_FOR_CLIENT_TO_UPLOAD_DOCUMENTS:
        case AssignmentStage.REQUEST_SENT_TO_PROCESSOR:
        case AssignmentStage.DOCUMENTS_APPROVED_FROM_CLIENT:
          data[1].activeStep = true
          break
        case AssignmentStage.DOCUMENTS_RECEIVED_FROM_PROCESSOR:
          data[2].activeStep = true
          break

        case AssignmentStage.DOCUMENTS_SENT_TO_CLIENT:
          data[3].activeStep = true
          break

        default:
          break
      }
    }
  } else {
    return []
  }

  return data
}
